import { useEffect, useState } from 'react';

import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { LayoutDashboard, LogOut, Menu, Search, Shield, Users, X } from 'lucide-react';

import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { isRestaurantVertical } from '@/lib/vertical';

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { NotificationBell } from '@/components/NotificationBell';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

import {
  getCrmMenuSections,
  openCrmPalette,
  parseActiveBusinessId,
} from '@/pages/crm/crmNavConfig';

export default function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [crmVertical, setCrmVertical] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  const businessId = parseActiveBusinessId(pathname);
  const inCrm = Boolean(businessId);
  const crmNavLabel = isRestaurantVertical(crmVertical) ? 'Insights' : 'CRM';
  const crmSections = businessId ? getCrmMenuSections(businessId, crmVertical) : [];

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/crm', label: crmNavLabel, icon: Users },
    ...(isPlatformAdmin ? [{ to: '/admin', label: 'Admin', icon: Shield }] : []),
  ];

  useEffect(() => {
    let cancelled = false;
    api.admin.me()
      .then(({ is_admin }) => {
        if (!cancelled) setIsPlatformAdmin(is_admin);
      })
      .catch(() => {
        if (!cancelled) setIsPlatformAdmin(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!businessId) {
      setCrmVertical(null);
      return;
    }
    let cancelled = false;
    api.getBusiness(businessId)
      .then(({ business }) => {
        if (!cancelled) setCrmVertical(business.vertical ?? 'trades');
      })
      .catch(() => {
        if (!cancelled) setCrmVertical('trades');
      });
    return () => { cancelled = true; };
  }, [businessId]);

  function closeMenu() {
    setMenuOpen(false);
  }

  async function handleSignOut() {
    closeMenu();
    await signOut();
    navigate('/login');
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-zinc-800/60 bg-zinc-950/95 backdrop-blur-md">
        <div className="mx-auto flex h-11 items-center justify-between gap-2 px-3 sm:h-14 sm:gap-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-6">
            <Link
              to="/dashboard"
              className="group flex min-w-0 items-center gap-2 font-semibold tracking-tight text-white sm:gap-2.5"
            >
              <img
                src="/brand/logo.jpg"
                alt=""
                aria-hidden="true"
                className="size-6 shrink-0 rounded-lg ring-1 ring-white/10 sm:size-8"
              />
              <span className="truncate text-sm sm:text-base">Dispafy</span>
            </Link>
            <nav className="hidden items-center gap-1 sm:flex">
              {navItems.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition',
                        isActive
                          ? 'bg-zinc-900 text-white'
                          : 'text-zinc-400 hover:bg-zinc-900/60 hover:text-white',
                      )
                    }
                  >
                    <Icon className="size-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-0.5 text-sm sm:gap-2">
            {inCrm && businessId && (
              <>
                <button
                  type="button"
                  onClick={openCrmPalette}
                  className="inline-flex size-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white md:hidden"
                  aria-label="Search workspace"
                >
                  <Search className="size-4" />
                </button>
                <div className="md:hidden">
                  <NotificationBell businessId={businessId} />
                </div>
              </>
            )}
            <span className="hidden max-w-[12rem] truncate text-zinc-400 md:inline">{user?.email}</span>
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              className="inline-flex size-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white md:hidden"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white sm:inline-flex"
            >
              <LogOut className="size-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav
            className="max-h-[min(75vh,36rem)] overflow-y-auto border-t border-zinc-800/60 px-3 py-2 md:hidden"
            aria-label="Mobile navigation"
          >
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition',
                      isActive
                        ? 'bg-zinc-900 text-white'
                        : 'text-zinc-400 hover:bg-zinc-900/60 hover:text-white',
                    )
                  }
                >
                  <Icon className="size-4" />
                  {item.label}
                </NavLink>
              );
            })}

            {inCrm && crmSections.length > 0 && (
              <div className="mt-2 border-t border-zinc-800/60 pt-2">
                <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  Workspace
                </p>
                {crmSections.map(section => {
                  const Icon = section.icon;
                  const hasSub = section.items.length > 0;
                  return (
                    <div key={section.id} className="mb-0.5">
                      <NavLink
                        to={section.primaryTo}
                        onClick={closeMenu}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition',
                            isActive && !hasSub
                              ? 'bg-cyan-500/10 text-cyan-100'
                              : 'text-zinc-200 hover:bg-zinc-900/60 hover:text-white',
                          )
                        }
                      >
                        <Icon className="size-4 shrink-0" aria-hidden />
                        {section.label}
                      </NavLink>
                      {hasSub &&
                        section.items.map(item => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={closeMenu}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center rounded-lg py-2 pl-10 pr-3 text-sm transition',
                                isActive
                                  ? 'bg-cyan-500/10 text-cyan-100'
                                  : 'text-zinc-400 hover:bg-zinc-900/60 hover:text-zinc-200',
                              )
                            }
                          >
                            {item.label}
                          </NavLink>
                        ))}
                    </div>
                  );
                })}
              </div>
            )}

            {user?.email && (
              <p className="mt-2 truncate border-t border-zinc-800/60 px-3 pt-2 text-xs text-zinc-500">
                {user.email}
              </p>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-zinc-400 hover:bg-zinc-900/60 hover:text-white sm:hidden"
            >
              <LogOut className="size-4" />
              Sign out
            </button>
          </nav>
        )}
      </header>

      <ImpersonationBanner />

      <main
        className={cn(
          'mx-auto max-w-7xl px-3 py-3 sm:px-6 sm:py-10',
          inCrm && 'max-md:px-3 max-md:py-2',
        )}
      >
        {children}
      </main>
    </div>
  );
}
